<template>
    <AppModal @close="onClose()" v-bind="$attrs" v-if="visible">
        <template v-slot:header>Edit {{ spaceType }} {{ spaceIdentity }}</template>
        <form @submit.prevent="onSubmit()">
            <transition name="fade">
                <AppNotification :type="alertMessage.type" v-if="alertMessage.message">
                    {{alertMessage.message}}
                </AppNotification>
            </transition>
            <div class="form-group">
                <label for="spaceIdentity">{{ spaceType }} Number</label>
                <input :class="{'is-invalid': $v.spaceIdentity.$invalid && isValidating}"
                       class="form-control"
                        maxlength="30"
                        v-model="spaceIdentity"/>
                <span class="invalid-feedback" v-if="!$v.spaceIdentity.required && isValidating">Field is required</span>
                <span class="invalid-feedback" v-if="!$v.spaceIdentity.alphaNum && isValidating">Field cannot include special characters</span>
            </div>
            <b-form-checkbox v-model="hasParentBuildingSpace" name="check-button" switch class="pb-3">
                Include Parent Room/Suite
            </b-form-checkbox>
            <div v-if="hasParentBuildingSpace" class="form-group">
                <label for="parentBuildingSpaceId">Parent Room/Suite</label>
                <select class="form-control" v-model="parentBuildingSpaceId">
                    <option :value="null" disabled>--Select Parent--</option>
                    <option :key="parentBuildingSpace.id" :value="parentBuildingSpace.id" v-for="parentBuildingSpace in possibleParentBuildingSpaces">{{parentBuildingSpace.spaceType}} {{parentBuildingSpace.spaceIdentity}}</option>
                </select>
            </div>
        </form>
        <template v-slot:footer>
            <button :disabled="isSubmitting" @click.prevent="onSubmit()" class="btn btn-primary" type="submit">Save
            </button>
            <button :disabled="isSubmitting" @click="onClose()" class="btn btn-danger" data-dismiss="modal"
                    type="button">
                Close
            </button>
        </template>
    </AppModal>
</template>

<script>
    import AppModal from "../shared/AppModal";
    import AppNotification from "@/components/shared/AppNotification";
    import BuildingSpaceService from "@/services/BuildingSpaceService";
    import {required, alphaNum} from "vuelidate/lib/validators";
    import {ToastType} from "@/services/models/shared/ToastType";

    export default {
        components: {
            AppModal,
            AppNotification
        },
        data() {
            return {
                hasParentBuildingSpace: false,
                possibleParentBuildingSpaces: [],
                parentBuildingSpacesFilterOptions: {
                    returnAll: true,
                    page: 1,
                    pageSize: 10,
                    buildingId: this.buildingId,
                    spaceType: null,
                    parentSpaceType: null,
                    spaceIdentity: null,
                    excludeBuildingSpaceId: this.id
                },
                isSubmitting: false,
                isValidating: false,
                spaceTypes: [
                    {
                        id: 1,
                        name: 'Room'
                    },
                    {
                        id: 2,
                        name: 'Suite'
                    }
                ],
                alertMessage: {
                    type: null,
                    message: null
                },
                parentBuildingSpaceId: null,
                spaceIdentity: null,
                spaceType: null
            }
        },
        props: {
            id: {
                type: String,
                required: true,
            },
            buildingId: {
              type: String,
              required: true
            },
            visible: {
                required: false,
                default: false
            }
        },
        watch: {
            visible() {
                this.possibleParentBuildingSpaces = [];
                this.hasParentBuildingSpace = false;
            },
            /**
             * @description Watches the hasParentBuildingSpace for check. If it is checked it gets the list of parent building spaces
             */
            hasParentBuildingSpace(newVal) {
                if (newVal) {
                  const vm = this;
                  BuildingSpaceService.getBuildingSpaces(this.buildingId, this.parentBuildingSpacesFilterOptions)
                    .then(response => {
                      vm.possibleParentBuildingSpaces = response.data;
                    }).catch(() => {
                        this.parentBuildingSpaceId = null;
                        vm.possibleParentBuildingSpaces = [];
                        vm.alertMessage.type = "danger";
                        vm.alertMessage.message = "Unable to load list of parent rooms/suites for this building.";
                    });
                    return;
                }
                this.parentBuildingSpaceId = null;
                this.possibleParentBuildingSpaces = [];
            }
        },
        methods: {
            loadData () {
              const vm = this;

              BuildingSpaceService.getBuildingSpace(this.id)
                  .then(response => {
                      this.spaceType = response.spaceType;
                      this.spaceIdentity = response.spaceIdentity;
                      this.parentBuildingSpaceId = response.parentBuildingSpaceId;
                      if (this.parentBuildingSpaceId !== null) {
                        this.hasParentBuildingSpace = true;
                      }
                  })
                  .catch(error => {
                      vm.alert(ToastType.Danger, error.response.data)

                      vm.onClose();
                  })
            },

            /**
             * @description Emits the close event to the parent
             */
            onClose() {
                this.hasParentBuildingSpace = false;
                this.parentBuildingSpaceId = null;
                this.possibleParentBuildingSpaces = [];
                this.spaceIdentity = null;
                this.spaceType = null;
                this.isValidating = false;
                this.isSubmitting = false;
                this.alertMessage = {
                    type: null,
                    message: null
                }
                this.$emit('close')
            },

            /**
             * @description Updates a room/suite and emits the event to the parent
             */
            onSubmit() {
                const vm = this;
                vm.isValidating = true;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                BuildingSpaceService.updateBuildingSpace(this.id, {
                  spaceIdentity: this.spaceIdentity,
                  parentBuildingSpaceId: this.parentBuildingSpaceId
                })
                    .then(() => {
                      vm.alert(ToastType.Success, "The room/suite was successfully updated.");

                      vm.$emit('create');
                    })
                    .catch((error) => {
                      vm.alert(ToastType.Danger, error.response.data)
                    })
                    .finally(() => {
                      vm.onClose();
                    })
            }
        },
        created () {
            this.loadData();
        },
        validations: {
            spaceIdentity: {
                required,
                alphaNum
            }
        }
    }
</script>
